import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartDiscountUpdateEvent } from '@shopify/events';

/** Preserve unrelated codes, including a better discount the customer entered. */
export function studentDiscountCodes(cart, ownCode, remove) {
  const codes = (cart.discount_codes || []).map((entry) => entry.code).filter((code) => typeof code === 'string');
  const others = codes.filter((code) => code.toUpperCase() !== ownCode.toUpperCase());
  return remove ? others : [...others, ownCode];
}

export function hasOtherMerchandiseDiscount(cart, ownCode) {
  const applications = [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap((item) =>
      (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application)
    ),
  ];
  return applications.some((application) => application &&
    application.target_type !== 'shipping_line' &&
    String(application.title || application.code || '').toUpperCase() !== ownCode.toUpperCase()
  );
}

class StudentDiscount extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  showMessage(text, error = false) {
    const message = this.querySelector('[data-student-message]');
    if (!message) return;
    message.textContent = text;
    message.dataset.error = String(error);
    message.hidden = !text;
  }

  #onClick = async (event) => {
    const button = event.target.closest('[data-student-action]');
    if (!button || button.disabled || !this.contains(button) || this.#busy) return;
    event.preventDefault();
    const code = this.dataset.code;
    const sectionId = this.dataset.sectionId;
    if (!code || !sectionId) return;

    const remove = button.dataset.studentAction === 'remove';
    const root = window.Shopify?.routes?.root || '/';
    const originalLabel = button.textContent;
    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.textContent = remove ? 'Wird entfernt …' : 'Wird aktiviert …';
    this.showMessage('');
    let deferred;
    let changedCart;
    let shippingProtected = false;

    try {
      const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!currentResponse.ok) throw new Error('cart-read');
      const currentCart = await currentResponse.json();
      if (!currentCart.item_count) throw new Error('empty-cart');
      if (!remove && hasOtherMerchandiseDiscount(currentCart, code)) throw new Error('discount-conflict');

      const codes = studentDiscountCodes(currentCart, code, remove);
      deferred = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(new CartDiscountUpdateEvent({
        discountCodes: codes.map((entry) => ({ code: entry })),
        promise: deferred.promise,
      }));

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: [sectionId], sections_url: window.location.pathname }),
      });
      if (!response.ok) throw new Error('cart-update');
      changedCart = await response.json();

      // Read the same server-rendered threshold as the announcement. The planned
      // campaign change updates its Liquid value, so this cannot retain an old limit.
      const announcement = document.querySelector('[data-free-shipping-threshold]');
      const shippingThreshold = Number(announcement?.dataset.freeShippingThreshold);
      if (!remove && currentCart.currency === 'EUR' && shippingThreshold > 0 &&
          currentCart.total_price >= shippingThreshold && changedCart.total_price < shippingThreshold) {
        const restoreResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: studentDiscountCodes(currentCart, code, true).join(','),
            sections: [sectionId], sections_url: window.location.pathname,
          }),
        });
        if (!restoreResponse.ok) throw new Error('cart-restore');
        changedCart = await restoreResponse.json();
        shippingProtected = true;
      }

      const isDrawer = Boolean(this.closest('theme-drawer'));
      const html = changedCart.sections?.[sectionId];
      if (typeof html === 'string') {
        if (isDrawer && typeof window.updateCartDrawerDOM === 'function') {
          window.updateCartDrawerDOM(html);
        } else {
          await morphSection(sectionId, html, { mode: isDrawer ? 'hydration' : 'full' });
        }
      } else {
        await sectionRenderer.renderSection(sectionId, { cache: false, mode: isDrawer ? 'hydration' : 'full' });
      }

      const replacement = [...document.querySelectorAll('mm-student-discount')]
        .find((element) => element.dataset.sectionId === sectionId);
      const applicable = changedCart.discount_codes?.some((entry) =>
        entry.code.toUpperCase() === code.toUpperCase() && entry.applicable === true
      );
      const succeeded = remove ? !applicable : applicable;
      const notice = shippingProtected
        ? 'Mit 5 % würdest du die Freiversandgrenze unterschreiten. Der Rabatt wurde deshalb nicht aktiviert.'
        : succeeded
        ? (remove ? 'Studentenrabatt entfernt.' : '5 % aktiviert. Deine Preise wurden aktualisiert.')
        : '5 % gelten nur auf reguläre Artikel und sind nicht mit anderen Produktrabatten kombinierbar.';

      replacement?.showMessage?.(notice, !succeeded);
      replacement?.querySelector('[data-student-action]')?.focus({ preventScroll: true });
      deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(changedCart) });
      deferred = undefined;
    } catch (error) {
      const text = error.message === 'empty-cart'
        ? 'Lege zuerst eine Tasse in deinen Warenkorb.'
        : error.message === 'discount-conflict'
        ? 'Dein Produktrabatt bleibt erhalten. Der Studentenrabatt ist damit nicht kombinierbar.'
        : 'Das hat gerade nicht geklappt. Bitte versuche es erneut.';
      this.showMessage(text, true);
      // The native listeners settle even when the request or section render fails.
      if (deferred) {
        if (changedCart) {
          // A failed rollback must not leave old prices on screen after a successful mutation.
          await sectionRenderer.renderSection(sectionId, {
            cache: false, mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          }).catch(() => {});
          const replacement = [...document.querySelectorAll('mm-student-discount')]
            .find((element) => element.dataset.sectionId === sectionId);
          replacement?.showMessage?.('Bitte prüfe die aktualisierten Preise und Versandkosten vor dem Bezahlen.', true);
          deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(changedCart) });
        }
        else deferred.reject(error);
      }
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      button.disabled = false;
      // Native morphing can reuse this button while changing it to "Entfernen".
      // Restore a loading label only if the rendered action has not changed.
      if (button.dataset.studentAction === (remove ? 'remove' : 'apply')) button.textContent = originalLabel;
    }
  };
}

if (!customElements.get('mm-student-discount')) customElements.define('mm-student-discount', StudentDiscount);
